export default class GameController {
	// Tracks if the game has been solved
	_gameSolved = { value: false };

	// Mapping of pattern keys to their corresponding win values
	_patternMap = {
		1: { key: "_win_1", value: 1 },
		2: { key: "_win_2", value: 2 },
		4: { key: "_win_4", value: 4 },
		X: { key: "_win_10", value: 10 },
		T: { key: "_win_20", value: 20 },
		F: { key: "_win_40", value: 40 },
		C: { key: "_win_100", value: 100 },
		B: { key: "_win_5000", value: 5000 },
	};

	_winFieldType = undefined;

	constructor({ nngGameModel }) {
		this._model = nngGameModel;
		this._fields = this._resolvePatternKeys();
		this._winFieldType = this.getWinFieldType();
	}

	// Extracts individual pattern keys from the model, keeping only keys that map
	// to a known win value. A broken, empty or placeholder pattern (e.g. an
	// unresolved "${tPattern}") therefore yields no fields instead of crashing.
	_patternKeys() {
		return (this._model.pattern?.match(/.{1}/g) || []).filter(
			(key) => key in this._patternMap,
		);
	}

	// Resolves pattern keys into a structured field object
	_resolvePatternKeys() {
		return this._patternKeys().reduce((acc, key, index) => {
			acc[`f${index + 1}`] = {
				value: `f${index + 1}${this.getPatternValue(key)?.key}`,
				key: this.getPatternValue(key)?.key,
				isEreased: false,
			};
			return acc;
		}, {});
	}

	// Returns the current game fields
	get fields() {
		return this._fields;
	}

	// Checks if the game is solved
	get gameSolved() {
		return this._gameSolved;
	}

	// Returns the game model instance
	get model() {
		return this._model;
	}

	getWinFieldType(winCount = 3) {
		const fields = {};
		for (const key in this.fields) {
			const val = this.fields[key].key;
			fields[val] = {
				count: (fields[val]?.count || 0) + 1,
				field: this.fields[key].key,
			};
		}
		const key = Object.keys(fields).find(
			(key) => fields[key].count >= winCount,
		);
		return key !== undefined ? fields[key].field : undefined;
	}

	// Initializes SVG and eraser functionality
	initSVG(SVG, Ereaser) {
		this.SVG = SVG;
		this.Ereaser = Ereaser;
		this.initGame();
	}

	gamePlayedAction() {
		this._model.isGamePlayed = true;
	}

	// Sets the game as active or inactive
	setIsGameActive(value) {
		this._model.isGameActive = value;
	}

	// Retrieves pattern value by key, or null for unknown keys (callers use ?.)
	getPatternValue(key) {
		return this._patternMap[key] ?? null;
	}

	// Initializes the game mechanics
	initGame() {
		this.SVG.addHideClassBySlug("_win_");

		this.Ereaser.init("_overlay");
		this.Ereaser.listenOnErased((element) => {
			this.SVG.addClass(element, "nng-hide");

			const fieldId = this.getFieldIdFromElement(element);
			//this.SVG.toggleClass(this.fields[fieldId].value);
			this.SVG.addClass(this.fields[fieldId].value, "nng-show");

			if (this.winFieldType === this.fields[fieldId].key) {
				this.SVG.addClass(this.fields[fieldId].value, "nng-win");
			}

			this._fields[fieldId].isEreased = true;
			this._gameSolved.value = this.isGameSolved();
		});
	}

	// Determines if all fields have been erased
	isGameSolved() {
		for (const key of Object.keys(this._fields)) {
			if (!this._fields[key].isEreased) {
				return false;
			}
		}
		return true;
	}

	// Extracts field ID from an erased element
	getFieldIdFromElement(element) {
		return element.id.split("_")[0];
	}

	get winFieldType() {
		return this._winFieldType;
	}
}
